import { useCallback, useEffect, useState } from 'react';
import { makeStyles } from 'tss-react/mui';

import FilterListIcon from '@mui/icons-material/FilterList';
import HistoryIcon from '@mui/icons-material/History';
import { Box, MenuItem, Select, Theme, Typography } from '@mui/material';

import { getPayoutChangeHistory } from '../../../api/clientsApi/clientsApi';
import FormLinkButton from '../../../components/buttons/FormLinkButton';
import Loader from '../../../components/Loader';
import InactiveUserName from '../../../components/InactiveUserName';
import { HISTORY_FILTER_OPTIONS } from './constants';
import {
  detectChangeType,
  formatHistoryDate,
  formatReasonCode,
  getSettlementLabel,
  getSettlementPath,
  summariseHistoryValue,
} from './helpers';
import { HistoryFilter, PayoutChangeHistoryItem, SettlementPayoutSummary } from './types';

const useStyles = makeStyles()((theme: Theme) => ({
  fieldHeadline: {
    color: theme.palette.common.white,
    fontFamily: 'Inter',
    fontSize: '20px',
    fontWeight: 600,
    lineHeight: '24px',
  },
  filterSelect: {
    height: '36px',
    minWidth: '160px',
    background: 'rgba(255, 255, 255, 0.03)',
    borderRadius: '10px',
    color: theme.palette.common.white,
    fontFamily: 'Inter',
    fontSize: '13px',
    fontWeight: 500,

    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.secondary.dark,
      borderRadius: '10px',
    },

    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.info.main,
    },

    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.info.main,
    },

    '& .MuiSelect-select': {
      padding: '6px 12px',
      color: theme.palette.common.white,
      fontFamily: 'Inter',
      fontSize: '13px',
    },

    '& .MuiSvgIcon-root': {
      color: theme.palette.text.disabled,
    },
  },
  menuPaper: {
    backgroundColor: theme.palette.secondary.dark,

    '& .MuiMenuItem-root': {
      color: theme.palette.common.white,
      fontFamily: 'Inter',
      fontSize: '14px',
      fontWeight: 500,
    },
  },
  historyItem: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
    padding: '16px',
    border: `1px solid ${theme.palette.secondary.dark}`,
    borderRadius: '12px',
  },
  historyBadge: {
    padding: '2px 7px',
    borderRadius: '4px',
  },
  settlementBadge: {
    backgroundColor: 'rgba(0, 102, 124, 0.25)',
    border: `1px solid ${theme.palette.info.main}`,
  },
  allSettlementsBadge: {
    backgroundColor: 'rgba(74, 78, 93, 0.75)',
  },
  historyBadgeText: {
    color: theme.palette.common.white,
    fontFamily: 'Inter',
    fontSize: '10px',
    fontWeight: 700,
    letterSpacing: '0.5px',
    textTransform: 'capitalize',
  },
  historyMetaText: {
    color: 'rgba(255, 255, 255, 0.45)',
    fontFamily: 'Inter',
    fontSize: '13px',
  },
  historyUserText: {
    color: 'rgba(255, 255, 255, 0.7)',
    fontFamily: 'Inter',
    fontSize: '13px',
  },
  historyLabelText: {
    minWidth: '36px',
    color: 'rgba(255, 255, 255, 0.35)',
    fontFamily: 'Inter',
    fontSize: '12px',
    fontWeight: 600,
  },
  historyPriorText: {
    color: 'rgba(255, 255, 255, 0.45)',
    fontFamily: 'Inter',
    fontSize: '12px',
  },
  historyNewText: {
    color: theme.palette.common.white,
    fontFamily: 'Inter',
    fontSize: '12px',
    fontWeight: 500,
  },
  historyReasonText: {
    marginTop: '6px',
    color: 'rgba(255, 255, 255, 0.4)',
    fontFamily: 'Inter',
    fontSize: '12px',
  },
  historyNoteText: {
    marginTop: '2px',
    color: 'rgba(255, 255, 255, 0.45)',
    fontFamily: 'Inter',
    fontSize: '12px',
    fontStyle: 'italic',
  },
  emptyTableText: {
    padding: '32px 0',
    color: 'rgba(255, 255, 255, 0.75)',
    fontFamily: 'Inter',
    fontSize: '14px',
    fontWeight: 400,
    textAlign: 'center',
  },
}));

interface ClientPayoutChangeHistoryContainerProps {
  clientId: string;
  refreshKey: number;
  settlements?: SettlementPayoutSummary[];
}

const ClientPayoutChangeHistoryContainer = ({
  clientId,
  refreshKey,
  settlements,
}: ClientPayoutChangeHistoryContainerProps) => {
  const styles = useStyles();

  const [historyLoading, setHistoryLoading] = useState(false);
  const [changeHistory, setChangeHistory] = useState<PayoutChangeHistoryItem[]>([]);
  const [historyFilter, setHistoryFilter] = useState<HistoryFilter>('ALL');
  const [settlementFilter, setSettlementFilter] = useState<number | 'ALL'>('ALL');

  const fetchHistory = useCallback(() => {
    setHistoryLoading(true);

    getPayoutChangeHistory(clientId, {
      page: 1,
      size: 50,
      ...(historyFilter !== 'ALL' && { paymentMethod: historyFilter }),
      ...(settlementFilter !== 'ALL' && { settlementId: settlementFilter as number }),
    })
      .then((res) => setChangeHistory(res.data.resultSet ?? []))
      .catch(() => setChangeHistory([]))
      .finally(() => setHistoryLoading(false));
  }, [clientId, historyFilter, settlementFilter]);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory, refreshKey]);

  return (
    <>
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        mb="20px"
        gap="12px"
      >
        <Box display="flex" alignItems="center" gap="10px">
          <HistoryIcon sx={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '22px' }} />
          <Typography className={styles.classes.fieldHeadline}>Change History</Typography>
        </Box>
        <Box display="flex" alignItems="center" gap="8px" flexWrap="wrap">
          <FilterListIcon sx={{ color: 'rgba(255, 255, 255, 0.4)', fontSize: '18px' }} />
          <Select
            className={styles.classes.filterSelect}
            MenuProps={{ classes: { paper: styles.classes.menuPaper } }}
            onChange={(e) => setHistoryFilter(e.target.value as HistoryFilter)}
            value={historyFilter}
          >
            {HISTORY_FILTER_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
          {settlements && settlements.length > 0 && (
            <Select
              className={styles.classes.filterSelect}
              MenuProps={{ classes: { paper: styles.classes.menuPaper } }}
              onChange={(e) => setSettlementFilter(e.target.value as number | 'ALL')}
              value={settlementFilter}
            >
              <MenuItem value="ALL">All Settlements</MenuItem>
              {settlements.map((s) => (
                <MenuItem key={s.settlementId} value={s.settlementId}>
                  {getSettlementLabel(s.settlementId, s.caseReferenceId)}
                </MenuItem>
              ))}
            </Select>
          )}
        </Box>
      </Box>

      {historyLoading ? (
        <Box py="24px">
          <Loader colorType="info" size={40} />
        </Box>
      ) : changeHistory.length === 0 ? (
        <Typography className={styles.classes.emptyTableText}>No changes recorded.</Typography>
      ) : (
        <Box display="flex" flexDirection="column" gap="12px">
          {changeHistory.map((item, index) => {
            const changeType = detectChangeType(item);
            const badgeLabel =
              changeType === 'ACH'
                ? 'ACH details'
                : changeType === 'CHECK'
                ? 'Mailing address'
                : 'Preferred mode';
            const badgeColor =
              changeType === 'ACH'
                ? 'rgba(0, 102, 124, 0.45)'
                : changeType === 'CHECK'
                ? 'rgba(74, 78, 93, 0.75)'
                : 'rgba(56, 45, 100, 0.65)';
            const hasPrior = Object.keys(item.originalData).length > 0;
            const hasNew = Object.keys(item.updatedData).length > 0;
            const mergedUpdatedData =
              changeType === 'CHECK'
                ? { ...item.originalData, ...item.updatedData }
                : item.updatedData;
            const priorText = hasPrior
              ? summariseHistoryValue(item.originalData, changeType)
              : '—';
            const newText = hasNew
              ? summariseHistoryValue(mergedUpdatedData, changeType)
              : '—';
            const hasPriorText = priorText !== '—';
            const reasonLabel = formatReasonCode(item.reasonCode);
            const formattedDate = formatHistoryDate(item.updatedAt);

            return (
              <Box
                key={`${item.updatedAt}-${index}`}
                className={styles.classes.historyItem}
              >
                <Box display="flex" alignItems="center" gap="8px" flexWrap="wrap">
                  <Box
                    className={styles.classes.historyBadge}
                    sx={{ backgroundColor: badgeColor }}
                  >
                    <Typography className={styles.classes.historyBadgeText}>
                      {badgeLabel}
                    </Typography>
                  </Box>
                  {typeof item.settlementId === 'number' ? (
                    <Box display="flex" alignItems="center" gap="4px">
                      <Box
                        className={`${styles.classes.historyBadge} ${styles.classes.settlementBadge}`}
                      >
                        <Typography className={styles.classes.historyBadgeText}>
                          {getSettlementLabel(item.settlementId, item.caseReferenceId)}
                        </Typography>
                      </Box>
                      <Box display="flex" sx={{ '& > a': { marginBottom: 0 } }}>
                        <FormLinkButton path={getSettlementPath(item.settlementId)} />
                      </Box>
                    </Box>
                  ) :item.appliesToAllSettlements ? (
                    <Box
                      className={`${styles.classes.historyBadge} ${styles.classes.allSettlementsBadge}`}
                    >
                      <Typography className={styles.classes.historyBadgeText}>
                        All settlements
                      </Typography>
                    </Box>
                  ) : null}
                  {formattedDate && (
                    <Typography className={styles.classes.historyMetaText}>
                      {formattedDate}
                    </Typography>
                  )}
                  {item.updatedByName && (
                    <>
                      <Typography
                        sx={{ color: 'rgba(255, 255, 255, 0.2)', fontSize: '13px' }}
                      >
                        &middot;
                      </Typography>
                      <Typography className={styles.classes.historyUserText}>
                        <InactiveUserName isInactive={item.updatedByIsDeactivated}>
                          <span>{item.updatedByName}</span>
                        </InactiveUserName>
                        {item.updatedByRole ? ` (${item.updatedByRole})` : ''}
                      </Typography>                    </>
                  )}
                </Box>

                <Box display="flex" flexDirection="column" gap="4px" mt="8px">
                  <Box display="flex" gap="6px">
                    <Typography className={styles.classes.historyLabelText}>Prior:</Typography>
                    <Typography
                      className={styles.classes.historyPriorText}
                      sx={{ fontStyle: hasPriorText ? 'normal' : 'italic' }}
                    >
                      {priorText}
                    </Typography>
                  </Box>
                  <Box display="flex" gap="6px">
                    <Typography className={styles.classes.historyLabelText}>New:</Typography>
                    <Typography className={styles.classes.historyNewText}>{newText}</Typography>
                  </Box>
                </Box>

                {reasonLabel && (
                  <Typography className={styles.classes.historyReasonText}>
                    Reason:{' '}
                    <Box
                      component="span"
                      sx={{ color: 'rgba(255, 255, 255, 0.65)', fontWeight: 500 }}
                    >
                      {reasonLabel}
                    </Box>
                  </Typography>
                )}
                {item.note && (
                  <Typography className={styles.classes.historyNoteText}>
                    &ldquo;{item.note}&rdquo;
                  </Typography>
                )}
              </Box>
            );
          })}
        </Box>
      )}
    </>
  );
};

export default ClientPayoutChangeHistoryContainer;
